import * as React from 'react'

import {
  TagColor as Color,
  StatusLabel,
  StatusLabelTypes,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { F0TagStatus, StatusVariant } from '@factorialco/f0-react'

import i18n from 'modules/core/lib/i18n'

import { useEsignatureF0 } from 'modules/documents/components/DocumentPreview/Content/Esignature/useEsignatureF0Context'
import type { EsignatureSignature } from 'modules/esignatures/hooks/signatures/useGetEsignatureSignatures'
import { SignatureStatuses } from 'modules/esignatures/lib/signatures/signatureStatuses'

interface Props {
  signature: EsignatureSignature
}

const StatusTooltip = ({ signature }: Props) => {
  const useF0 = useEsignatureF0()
  const [open, toggle] = useToggle(false)

  const status = signature.status as unknown as SignatureStatuses

  const statusToTagColor: Record<SignatureStatuses, Color> = {
    [SignatureStatuses.Created]: Color.champagne,
    [SignatureStatuses.Waiting]: Color.grey,
    [SignatureStatuses.Signed]: Color.viridian,
    [SignatureStatuses.Declined]: Color.radical,
    [SignatureStatuses.Bounced]: Color.radical,
    [SignatureStatuses.Cancelled]: Color.grey,
  }

  const statusToStatusLabelType: Record<SignatureStatuses, StatusLabelTypes> = {
    [SignatureStatuses.Created]: 'warning',
    [SignatureStatuses.Waiting]: 'neutral',
    [SignatureStatuses.Signed]: 'positive',
    [SignatureStatuses.Declined]: 'critical',
    [SignatureStatuses.Bounced]: 'critical',
    [SignatureStatuses.Cancelled]: 'neutral',
  }

  const statusLabel: Record<SignatureStatuses, string> = {
    [SignatureStatuses.Created]: i18n.t('documents.modal.esignature.status.created'),
    [SignatureStatuses.Waiting]: i18n.t('documents.modal.esignature.status.waiting'),
    [SignatureStatuses.Signed]: i18n.t('documents.modal.esignature.status.signed'),
    [SignatureStatuses.Declined]: i18n.t('documents.modal.esignature.status.declined'),
    [SignatureStatuses.Bounced]: i18n.t('documents.modal.esignature.status.bounced'),
    [SignatureStatuses.Cancelled]: i18n.t('documents.modal.esignature.status.cancelled'),
  }

  const tooltipLabel = (signature: EsignatureSignature) => {
    switch (signature.status) {
      case SignatureStatuses.Created:
        return i18n.t('documents.modal.esignature.document_created', {
          employee: signature.signee.fullName,
        })
      case SignatureStatuses.Waiting:
        return i18n.t('documents.modal.esignature.document_waiting', {
          employee: signature.signee.fullName,
        })
      case SignatureStatuses.Signed:
        return i18n.t('documents.modal.esignature.document_signed', {
          employee: signature.signee.fullName,
        })
      case SignatureStatuses.Declined:
        return i18n.t('documents.modal.esignature.document_declined', {
          employee: signature.signee.fullName,
        })
      case SignatureStatuses.Bounced:
        return i18n.t('documents.modal.esignature.document_bounced', {
          employee: signature.signee.fullName,
        })
      case SignatureStatuses.Cancelled:
        return i18n.t('documents.modal.esignature.document_cancelled')
      default:
        return i18n.t('documents.modal.esignature.status.created')
    }
  }

  const statusToF0Variant: Record<SignatureStatuses, StatusVariant> = {
    [SignatureStatuses.Created]: 'warning',
    [SignatureStatuses.Waiting]: 'neutral',
    [SignatureStatuses.Signed]: 'positive',
    [SignatureStatuses.Declined]: 'critical',
    [SignatureStatuses.Bounced]: 'critical',
    [SignatureStatuses.Cancelled]: 'neutral',
  }

  const statusTag = useF0 ? (
    <F0TagStatus text={statusLabel[status]} variant={statusToF0Variant[status]} />
  ) : (
    <StatusLabel ellipsis type={statusToStatusLabelType[status]}>
      {statusLabel[status]}
    </StatusLabel>
  )

  return (
    <Tooltip.Dialog
      attachment="bottom right"
      targetAttachment="top right"
      open={open}
      toggle={toggle}
      label={tooltipLabel(signature)}
    >
      {statusTag}
    </Tooltip.Dialog>
  )
}

export default StatusTooltip
